import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Course } from 'src/app/models/course';
import { Student } from 'src/app/models/student';
import { StudentEnrollment } from 'src/app/models/student-enrollment';
import { CoursesService } from 'src/app/services/courses/courses.service';
import { StudentsService, accessMailMessage } from 'src/app/services/students/students.service';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';

import { StudentEditComponent } from '../student-edit/student-edit.component';
import { StudentEnrollComponent } from '../student-enroll/student-enroll.component';

import { NamePipe } from '../../../pipes/name.pipe';
import { EnrollPipe } from '../../../pipes/enroll.pipe';
import { GenderPipe } from '../../../pipes/gender.pipe';
import { AddressPipe } from '../../../pipes/address.pipe';

@Component({
  selector: 'li-student-list',
  templateUrl: './student-list.component.html',
  styleUrls: ['./student-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatInputModule,
    StudentEditComponent,
    StudentEnrollComponent,
    NamePipe,
    EnrollPipe,
    GenderPipe,
    AddressPipe
  ]
})
export class StudentListComponent implements OnInit {
  public loading: boolean = true;
  public students: Student[] = new Array<Student>();
  public courses: Course[] = new Array<Course>();
  public selectedStudent?: Student;
  public enrollingStudent?: Student;
  public enrollments: StudentEnrollment[] = new Array<StudentEnrollment>();
  public dataSource = new MatTableDataSource<Student>();

  public constructor(
    private studentsService: StudentsService,
    private coursesService: CoursesService
  ) {}

  public ngOnInit() {
    this.dataSource.filterPredicate = function (record: any, filter: any) {
      if (filter.length < 3) {
        return true;
      }

      const searchIn =
        record.firstname?.toLocaleLowerCase() +
          record.lastname?.toLocaleLowerCase() +
          record.street?.toLocaleLowerCase() +
          record.email?.toLocaleLowerCase() || '';
      const searchFor = filter.toLocaleLowerCase() || '';

      return searchIn.indexOf(searchFor) >= 0;
    };

    this.getData();
  }

  public applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  public add(): void {
    this.selectedStudent = <Student>{};
  }

  public delete(student: Student): void {
    if (!confirm(`Soll der Student, namens "${student.firstname} ${student.lastname}" wirklich gelöscht werden?`)) {
      return;
    }

    this.studentsService.del(student).subscribe(_ => this.getData());
  }

  public sendAccess(student: Student): void {
    if (!confirm(`App-Zugang (QR-Code) für "${student.firstname} ${student.lastname}" erneut an ${student.email} senden?\n\nDer bisherige, noch nicht verwendete QR-Code wird damit ungültig.`)) {
      return;
    }

    this.studentsService.sendAccess(student).subscribe({
      next: response => alert(accessMailMessage(response.result, student.email)),
      error: () => alert(accessMailMessage('failed', student.email))
    });
  }

  public edit(student: Student): void {
    this.selectedStudent = student;
  }

  public enroll(student: Student): void {
    const enrollingStudent = Object.assign({}, student);

    enrollingStudent.enrollments = this.courses.map(
      c =>
        <StudentEnrollment>{
          cid: c.cid,
          name: c.name,
          diffname: c.diffname,
          begin:
            student?.enrollments.find(e => e.cid === c.cid)?.begin || undefined,
          end:
            student?.enrollments.find(e => e.cid === c.cid)?.end || undefined,
        }
    ).sort((a,b) => (a.begin === undefined ? 1 : (b.begin === undefined ? -1 : 0)));

    this.enrollingStudent = enrollingStudent;
  }

  public commit(): void {
    this.selectedStudent = undefined;
    this.getData();
  }

  public export(): void {
    this.studentsService.export().subscribe(async (response) => {
      const blob = new Blob([response], { type: 'attachment/csv' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");

      anchor.download = `${this.timestamp()}-li-dance-report.csv`;
      anchor.href = url;
      anchor.click();
      anchor.remove();
    });
  }

  private getData() {
    this.studentsService.get().subscribe({
      next: students => {
        this.loading = false;
        this.students = students;
        this.dataSource.data = this.students;
      },
    });

    this.coursesService.get().subscribe(c => (this.courses = c));
  }

  private timestamp(): string {
    const now = new Date();
  
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-based, so we add 1
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
  
    return `${year}${month}${day}${hours}${minutes}${seconds}`;
  }
}

